/**
 * Eine eigene Emoji-Auswahl — und warum es sie gibt.
 *
 * **Die Emoji-Tastatur des Geräts lässt sich nicht öffnen.** Es gibt dafür
 * keine Schnittstelle: eine Tastatur kommt, wenn ein Feld den Fokus hat, und
 * *welche Ebene* sie zeigt, entscheidet das Betriebssystem — in aller Regel
 * die zuletzt benutzte, also die Buchstaben. Aus dem Betrieb gemeldet:
 * „es wird die Standard-Tastatur geöffnet, man muss manuell auf Emoji
 * umschalten." Das ist keine Nachlässigkeit, sondern die Grenze der Umgebung.
 *
 * Also dasselbe Muster wie überall in diesem Projekt: **wir machen es
 * selbst.** Eine eigene Auswahl sieht auf jedem Gerät gleich aus, braucht kein
 * Umschalten und kein fremdes Paket — die Zeichen stehen hier, nicht in einer
 * Abhängigkeit.
 *
 * **Warum eine feste Liste und keine vollständige.** Vollständig wären es über
 * dreitausend Zeichen samt Namen und Suchindex; das ist ein halbes Megabyte,
 * das jeder Leser bei jedem Laden mitbekäme, für einen Reisebericht. Hier
 * stehen die, die in einer Antwort vorkommen. Fehlt eines, bleibt das Feld
 * darunter — dort tippt man es ein, und dann führt eben doch ein Weg über die
 * Tastatur.
 */
export interface Emojigruppe {
  /** Was auf dem Reiter steht — ein Zeichen, kein Wort: es muss schmal sein. */
  reiter: string
  /** Für Vorlesehilfen und den Tooltip. */
  name: string
  zeichen: string[]
}

export const EMOJIGRUPPEN: Emojigruppe[] = [
  {
    reiter: '😀',
    name: 'Gesichter',
    zeichen: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋',
      '😛', '🤪', '🤗', '🤭', '🤔', '🤨', '😐', '😑', '😶', '😏',
      '😒', '🙄', '😬', '😔', '😪', '🤤', '😴', '🥱', '😷', '🤒',
      '🥵', '🥶', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕',
      '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😢', '😭', '😱',
      '😖', '😞', '😤', '😠', '😡', '🤬', '😈', '💀', '🤡', '👻',
    ],
  },
  {
    reiter: '👍',
    name: 'Gesten',
    zeichen: [
      '👍', '👎', '👌', '🤌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈',
      '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤝',
      '🙏', '👏', '🙌', '👐', '🤲', '💪', '🦵', '👀', '👁️', '👣',
    ],
  },
  {
    reiter: '❤️',
    name: 'Herzen und Zeichen',
    zeichen: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '✨', '⭐',
      '🌟', '💫', '🔥', '💥', '💯', '❗', '❓', '⚠️', '✅', '❌',
    ],
  },
  {
    reiter: '🚐',
    name: 'Unterwegs',
    zeichen: [
      '🚐', '🚗', '🚙', '🛻', '🚚', '🚛', '🏍️', '🚲', '🛵', '🚂',
      '🚆', '✈️', '🛫', '🛬', '🚁', '⛵', '🛥️', '🚢', '⚓', '🧭',
      '🗺️', '🧳', '⛽', '🛣️', '🌉', '🏕️', '⛺', '🏞️', '🛖', '🏰',
    ],
  },
  {
    reiter: '🏔️',
    name: 'Natur und Wetter',
    zeichen: [
      '🏔️', '⛰️', '🌋', '🏜️', '🏝️', '🏖️', '🌊', '🌅', '🌄', '🌈',
      '☀️', '🌤️', '⛅', '🌦️', '🌧️', '⛈️', '🌩️', '❄️', '☃️', '🌬️',
      '🌙', '🌛', '🌜', '🌍', '🌲', '🌳', '🌴', '🌵', '🍀', '🌻',
      '🌸', '🍁', '🍂', '🐦', '🦌', '🐻', '🐕', '🐈', '🐴', '🐑',
    ],
  },
  {
    reiter: '🍽️',
    name: 'Essen und Trinken',
    zeichen: [
      '🍽️', '🍕', '🍔', '🌭', '🥪', '🌮', '🍟', '🥐', '🥖', '🧀',
      '🥗', '🍝', '🍜', '🍲', '🥘', '🍤', '🐟', '🥩', '🍗', '🥞',
      '🍰', '🎂', '🍦', '🍫', '🍿', '☕', '🍵', '🍺', '🍻', '🥂',
      '🍷', '🥃', '🧃', '💧', '🍎', '🍓', '🍇', '🍉', '🍌', '🥑',
    ],
  },
  {
    reiter: '🎉',
    name: 'Anlässe und Dinge',
    zeichen: [
      '🎉', '🎊', '🎈', '🎁', '🎄', '🎃', '🥇', '🏆', '🎯', '🎵',
      '🎶', '📷', '📸', '🎬', '📖', '✏️', '📝', '💡', '🔑', '🔒',
      '⏰', '📅', '💰', '💶', '🛒', '🧺', '🔦', '🪫', '🔋', '📌',
    ],
  },
  {
    reiter: '🇩🇪',
    name: 'Fahnen',
    zeichen: [
      '🇩🇪', '🇦🇹', '🇨🇭', '🇫🇷', '🇮🇹', '🇪🇸', '🇵🇹', '🇳🇱', '🇧🇪', '🇩🇰',
      '🇸🇪', '🇳🇴', '🇫🇮', '🇮🇸', '🇬🇧', '🇮🇪', '🇵🇱', '🇨🇿', '🇭🇷', '🇸🇮',
      '🇬🇷', '🇹🇷', '🇺🇸', '🇨🇦', '🇦🇺', '🇳🇿', '🇯🇵', '🇲🇦', '🏳️', '🏁',
    ],
  },
]
